import { useEffect } from 'react';
import { X } from 'lucide-react';
import { Button } from './ui/button';
import { isTenderTypeShown } from './shared/schema';
// Lightweight on-demand imports via dynamic import inside handlers

export default function ThermalPrintView({ data, tenderTypes, onClose }) {
  // Format date without timezone conversion (consistent with other components)
  const formatDateWithoutTimezone = (dateString) => {
    // Parse the date string (YYYY-MM-DD format) without timezone conversion
    const [year, month, day] = dateString.split('-').map(Number);
    const date = new Date(year, month - 1, day); // month is 0-indexed

    return date.toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
    });
  };

  // Format currency without symbol for cleaner look
  const formatAmount = (amount) => {
    if (amount === undefined || amount === null) return '0.00';
    const absAmount = Math.abs(amount);
    const formatted = absAmount.toLocaleString('en-US', {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    });
    return amount < 0 ? `-${formatted}` : formatted;
  };

  // Format currency with symbol
  const formatCurrency = (amount) => {
    if (amount === undefined || amount === null) return '$0.00';
    if (amount < 0) {
      return `-$${Math.abs(amount).toLocaleString('en-US', {
        minimumFractionDigits: 2,
        maximumFractionDigits: 2,
      })}`;
    }
    return `$${amount.toLocaleString('en-US', {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    })}`;
  };

  // Format negative currency with leading '-' (but show $0.00 for zero)
  const formatNegativeCurrency = (amount) => {
    if (!amount || Math.abs(amount) === 0) return '$0.00';
    return `-$${formatAmount(amount)}`;
  };

  // Format number with commas
  const formatNumber = (num) => {
    if (num === undefined || num === null) return '0';
    return num.toLocaleString('en-US');
  };

  // Center text (38 chars width for 80mm with padding)
  const center = (text, width = 38, bold = false) => {
    const spaces = Math.max(0, Math.floor((width - text.length) / 2));
    const content = ' '.repeat(spaces) + text;

    if (bold) {
      return <span style={{ fontWeight: 600 }}>{content}</span>;
    }
    return content;
  };

  // Right align text
  const rightAlign = (text, width = 38) => {
    const spaces = Math.max(0, width - text.length);
    return ' '.repeat(spaces) + text;
  };

  // Create line separator
  const line = (char = '-', width = 38) => {
    return char.repeat(width);
  };

  // Create two-column layout (label on left, value on right)
  const row = (label, value, width = 38, bold = false) => {
    const valueStr = value.toString();
    const spaces = Math.max(1, width - label.length - valueStr.length);
    const content = label + ' '.repeat(spaces) + valueStr;

    if (bold) {
      return <span style={{ fontWeight: 600 }}>{content}</span>;
    }
    return content;
  };

  // Handle keyboard events
  useEffect(() => {
    const handleEscape = (e) => {
      if (e.key === 'Escape') {
        onClose();
      }
    };
    window.addEventListener('keydown', handleEscape);
    return () => window.removeEventListener('keydown', handleEscape);
  }, [onClose]);

  // Handle print: open a dedicated print window with only the thermal content
  const handlePrint = () => {
    const content = document.querySelector('.thermal-print-content');
    if (!content) {
      window.print();
      return;
    }

    const printWindow = window.open('', '_blank', 'width=400,height=800');
    if (!printWindow) {
      window.print();
      return;
    }

    const html = `<!doctype html>
<html>
  <head>
    <meta charset="utf-8" />
    <meta name="viewport" content="width=device-width, initial-scale=1" />
    <title>Receipt</title>
    <style>
      @page { size: 80mm auto; margin: 0.25in; }
      html, body { background: #fff; color: #000; margin: 0; padding: 0; }
      .thermal-print-view { font-family: 'Roboto Mono', 'Courier New', monospace; font-weight: 400; }
      pre { font-family: 'Roboto Mono', 'Courier New', monospace; font-size: 14px; line-height: 1.5; margin: 0 auto; white-space: pre; word-wrap: break-word; overflow-x: hidden; box-sizing: border-box; max-width: 22rem; }
      .wrapper { display: flex; justify-content: center; padding: 16px; }
      @media print { .wrapper { padding: 0; } }
    </style>
  </head>
  <body>
    <div class="wrapper">${content.innerHTML}</div>
    <script>
      window.onload = function() { window.print(); setTimeout(() => window.close(), 300); };
    </script>
  </body>
 </html>`;

    printWindow.document.open();
    printWindow.document.write(html);
    printWindow.document.close();
  };

  // Handle PDF download (TEXT): letter-size, text-based, section-aligned page breaks
  const handleDownloadPdfText = async () => {
    const container = document.querySelector(
      '.thermal-print-content .thermal-print-view'
    );
    if (!container) return;

    const jsPDFModule = await import('jspdf');
    const jsPDF = jsPDFModule.jsPDF || jsPDFModule.default;

    // Letter size and layout
    const pageWidthMm = 215.9;
    const pageHeightMm = 279.4;
    const marginMm = 12.7; // 0.5in
    const contentWidthMm = 80; // 80mm receipt width

    const pdf = new jsPDF({ unit: 'mm', format: 'letter', orientation: 'p' });
    pdf.setFont('courier', 'normal');
    const fontSizePt = 12; // slightly larger for clarity
    pdf.setFontSize(fontSizePt);
    // Derive reliable line-height from jsPDF metrics (in current unit: mm)
    const metrics = pdf.getTextDimensions('Mg');
    const baseLineMm = metrics && metrics.h ? metrics.h : fontSizePt * 0.352777;
    const lineHeightMm = baseLineMm * 1.35; // padding to avoid overlap

    // Calculate fixed x position to center the entire receipt block (38 chars wide)
    // We center based on the width of a 38-character line in monospace font
    const receiptWidthChars = 38;
    const sampleLine = 'M'.repeat(receiptWidthChars); // Use 'M' as it's typically the widest char
    const receiptWidthMm = pdf.getTextWidth(sampleLine);
    const xStartMm = (pageWidthMm - receiptWidthMm) / 2;

    // Safe area of 100px ≈ 26.46mm
    const safeAreaMm = 10;
    const maxYPerPageMm = pageHeightMm - marginMm - safeAreaMm;

    // Extract lines from preformatted text
    const fullText = container.textContent || '';
    const allLines = fullText.split('\n');

    // Build sections that start at the FIRST '=' line of the header trio:
    // 0) '=====', 1) 'SECTION TITLE', 2) '====='
    const isSep = (s) => /^=+$/.test(s.trim());
    const sectionStarts = [0];
    for (let i = 0; i <= allLines.length - 3; i++) {
      const l0 = allLines[i];
      const l1 = allLines[i + 1];
      const l2 = allLines[i + 2];
      if (isSep(l0) && !isSep(l1) && isSep(l2)) {
        sectionStarts.push(i);
        i += 2; // skip past the header trio to avoid double-counting
      }
    }
    // Deduplicate and sort, ensure bounds
    const starts = Array.from(new Set(sectionStarts))
      .filter((i) => i >= 0 && i < allLines.length)
      .sort((a, b) => a - b);
    // Turn into [start,end) segments
    const sections = [];
    for (let s = 0; s < starts.length; s++) {
      const start = starts[s];
      const end = s + 1 < starts.length ? starts[s + 1] : allLines.length;
      if (end > start) sections.push({ start, end });
    }

    let yMm = marginMm; // current y
    let firstPage = true;

    const ensurePage = () => {
      if (!firstPage) pdf.addPage('letter', 'p');
      firstPage = false;
      yMm = marginMm;
    };

    // Determine lines that should be bold
    const boldLineIdxs = new Set();
    // Section titles (middle of the trio)
    for (let i = 0; i <= allLines.length - 3; i++) {
      const l0 = allLines[i];
      const l1 = allLines[i + 1];
      const l2 = allLines[i + 2];
      if (
        /^=+$/.test(l0.trim()) &&
        !/^=+$/.test(l1.trim()) &&
        /^=+$/.test(l2.trim())
      ) {
        boldLineIdxs.add(i + 1);
      }
    }
    // Business title line
    const businessTitle = (data.businessInfo?.businessTitle || '')
      .toString()
      .trim();
    if (businessTitle) {
      for (let i = 0; i < allLines.length; i++) {
        if (allLines[i].trim() === businessTitle) {
          boldLineIdxs.add(i);
        }
      }
    }
    // Labels that are bold in the original report rows
    const BOLD_LABELS = ['Amount Collected'];

    // Start with a fresh page to normalize y positioning
    ensurePage();

    // Render each section; fit as many whole sections per page as possible
    for (const section of sections) {
      const blockLines = section.end - section.start;
      const linesLeftOnPage = Math.floor((maxYPerPageMm - yMm) / lineHeightMm);
      const maxLinesOnFreshPage = Math.floor(
        (maxYPerPageMm - marginMm) / lineHeightMm
      );

      // If block fits in remaining space, render it entirely
      if (blockLines <= linesLeftOnPage && linesLeftOnPage > 0) {
        for (let j = section.start; j < section.end; j++) {
          const lineText = allLines[j] || '';
          const shouldBold =
            boldLineIdxs.has(j) ||
            BOLD_LABELS.some((lbl) => lineText.trim().startsWith(lbl));
          pdf.setFont('courier', shouldBold ? 'bold' : 'normal');
          pdf.text(lineText, xStartMm, yMm, { baseline: 'top' });
          yMm += lineHeightMm;
        }
        continue;
      }

      // If it doesn't fit but would fit on a fresh page, move to new page first
      if (blockLines <= maxLinesOnFreshPage) {
        ensurePage();
        for (let j = section.start; j < section.end; j++) {
          const lineText = allLines[j] || '';
          const shouldBold =
            boldLineIdxs.has(j) ||
            BOLD_LABELS.some((lbl) => lineText.trim().startsWith(lbl));
          pdf.setFont('courier', shouldBold ? 'bold' : 'normal');
          pdf.text(lineText, xStartMm, yMm, { baseline: 'top' });
          yMm += lineHeightMm;
        }
        continue;
      }

      // Otherfwise, render across multiple pages
      let i = section.start;
      while (i < section.end) {
        let remainingLinesHere = Math.floor(
          (maxYPerPageMm - yMm) / lineHeightMm
        );
        if (remainingLinesHere <= 0) {
          ensurePage();
          remainingLinesHere = Math.floor((maxYPerPageMm - yMm) / lineHeightMm);
        }

        // Keep the header trio together at top of page
        const isHeaderStart = i === section.start;
        const headerSize = Math.min(3, section.end - section.start);
        if (isHeaderStart && remainingLinesHere < headerSize) {
          ensurePage();
          continue;
        }

        const chunkEnd = Math.min(i + remainingLinesHere, section.end);
        for (let j = i; j < chunkEnd; j++) {
          const lineText = allLines[j] || '';
          const shouldBold =
            boldLineIdxs.has(j) ||
            BOLD_LABELS.some((lbl) => lineText.trim().startsWith(lbl));
          pdf.setFont('courier', shouldBold ? 'bold' : 'normal');
          pdf.text(lineText, xStartMm, yMm, { baseline: 'top' });
          yMm += lineHeightMm;
        }
        i = chunkEnd;
        if (i < section.end) {
          ensurePage();
        }
      }
    }

    // Filename
    const businessName = (
      data.businessInfo?.businessTitle || 'Business'
    ).toString();
    const reportType = 'POS-Performance-Summary';
    const startDate =
      data.dateRange?.startDate || new Date().toISOString().slice(0, 10);
    const endDate = data.dateRange?.endDate || startDate;
    const startTime = (data.filters?.startTime || '').replace(':', '-');
    const endTime = (data.filters?.endTime || '').replace(':', '-');
    const dateRangeStr =
      startTime && endTime
        ? `${startDate}_${startTime}-to-${endDate}_${endTime}`
        : `${startDate}-to-${endDate}`;
    const sanitize = (s) =>
      s
        .replace(/[^a-z0-9-_]+/gi, '_')
        .replace(/_+/g, '_')
        .replace(/^_+|_+$/g, '');
    const filename = `${sanitize(businessName)}_${reportType}_${sanitize(
      dateRangeStr
    )}.pdf`;
    pdf.save(filename);
  };

  // Handle PDF download (CLASSIC IMAGE): letter-size, 80mm centered, image-based split
  const handleDownloadPdf = async () => {
    const content = document.querySelector('.thermal-print-content');
    if (!content) return;

    const [{ default: html2canvas }, jsPDFModule] = await Promise.all([
      import('html2canvas'),
      import('jspdf'),
    ]);

    const pageWidthMm = 215.9;
    const pageHeightMm = 279.4;
    const marginMm = 12.7;
    const receiptWidthMm = 80;
    const contentHeightMm = pageHeightMm - marginMm * 2;
    const xOffsetMm = (pageWidthMm - receiptWidthMm) / 2;

    const canvas = await html2canvas(content, {
      scale: 2,
      backgroundColor: '#ffffff',
      useCORS: true,
      width: content.scrollWidth,
      height: content.scrollHeight,
    });

    const jsPDF = jsPDFModule.jsPDF || jsPDFModule.default;
    const pdf = new jsPDF({ unit: 'mm', format: 'letter', orientation: 'p' });

    const pxPerMm = canvas.width / receiptWidthMm;
    const pageContentHeightPx = contentHeightMm * pxPerMm;

    let currentY = 0;
    let pageIndex = 0;
    while (currentY < canvas.height) {
      const remaining = canvas.height - currentY;
      const segmentHeight = Math.min(remaining, pageContentHeightPx);

      const segmentCanvas = document.createElement('canvas');
      segmentCanvas.width = canvas.width;
      segmentCanvas.height = Math.max(1, Math.floor(segmentHeight));
      const ctx = segmentCanvas.getContext('2d');
      ctx.fillStyle = '#ffffff';
      ctx.fillRect(0, 0, segmentCanvas.width, segmentCanvas.height);
      ctx.drawImage(
        canvas,
        0,
        Math.floor(currentY),
        canvas.width,
        Math.floor(segmentHeight),
        0,
        0,
        canvas.width,
        Math.floor(segmentHeight)
      );

      const imgData = segmentCanvas.toDataURL('image/jpeg', 0.95);
      const segmentHeightMm = segmentHeight / pxPerMm;

      if (pageIndex > 0) pdf.addPage('letter', 'p');
      pdf.addImage(
        imgData,
        'JPEG',
        xOffsetMm,
        marginMm,
        receiptWidthMm,
        Math.min(segmentHeightMm, contentHeightMm),
        undefined,
        'FAST'
      );

      currentY += segmentHeight;
      pageIndex++;
      if (pageIndex > 200) break;
    }

    const businessName = (
      data.businessInfo?.businessTitle || 'Business'
    ).toString();
    const reportType = 'POS-Performance-Summary';
    const startDate =
      data.dateRange?.startDate || new Date().toISOString().slice(0, 10);
    const endDate = data.dateRange?.endDate || startDate;
    const startTime = (data.filters?.startTime || '').replace(':', '-');
    const endTime = (data.filters?.endTime || '').replace(':', '-');
    const dateRangeStr =
      startTime && endTime
        ? `${startDate}_${startTime}-to-${endDate}_${endTime}`
        : `${startDate}-to-${endDate}`;
    const sanitize = (s) =>
      s
        .replace(/[^a-z0-9-_]+/gi, '_')
        .replace(/_+/g, '_')
        .replace(/^_+|_+$/g, '');
    const filename = `${sanitize(businessName)}_${reportType}_${sanitize(
      dateRangeStr
    )}.pdf`;
    pdf.save(filename);
  };

  const tipSummary = data.tipReport || data.tipSummary;

  // Detect if this is the new POS Performance structure
  const isNewPosPerformance = data.todaysTotalOrders !== undefined;

  const showTender = (tenderType) => isTenderTypeShown(tenderTypes, tenderType);

  const renderPaymentMethodsSection = (title, paymentMethods) => (
    <>
      {line('=')}
      {'\n'}
      {center(title, 38, true)}
      {'\n'}
      {line('=')}
      {'\n\n'}
      {showTender('card') && paymentMethods.card !== undefined && (
        <>
          {row('CARD', formatCurrency(paymentMethods.card), 38, true)}
          {'\n\n'}
        </>
      )}
      {showTender('cash') && paymentMethods.cash !== undefined && (
        <>
          {row('CASH', formatCurrency(paymentMethods.cash), 38, true)}
          {'\n\n'}
        </>
      )}
      {showTender('other') && paymentMethods.other !== undefined && (
        <>
          {row('OTHER', formatCurrency(paymentMethods.other), 38, true)}
          {'\n\n'}
        </>
      )}
      {showTender('reward') &&
        paymentMethods.rewardsUsed !== undefined &&
        paymentMethods.rewardsUsed > 0 && (
          <>
            {row(
              'REWARDS USED',
              formatCurrency(paymentMethods.rewardsUsed),
              38,
              true
            )}
            {'\n\n'}
          </>
        )}
      {line()}
      {'\n'}
      {row(
        'TOTAL',
        formatCurrency(
          (showTender('card') ? paymentMethods.card || 0 : 0) +
            (showTender('cash') ? paymentMethods.cash || 0 : 0) +
            (showTender('other') ? paymentMethods.other || 0 : 0) +
            (showTender('reward') ? paymentMethods.rewardsUsed || 0 : 0)
        ),
        38,
        true
      )}
      {'\n\n'}
    </>
  );

  return (
    <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4 thermal-print-modal">
      <div className="bg-white rounded-lg shadow-xl max-w-2xl w-full max-h-[90vh] overflow-hidden flex flex-col thermal-print-container">
        {/* Header - No Print */}
        <div className="no-print flex items-center justify-between p-4 border-b">
          <h2 className="text-lg font-semibold">80mm Thermal Print Preview</h2>
          <div className="flex gap-2">
            <Button onClick={handleDownloadPdf} variant="secondary" size="sm">
              Download PDF (Classic)
            </Button>
            <Button
              onClick={handleDownloadPdfText}
              variant="secondary"
              size="sm"
            >
              Download PDF (Text)
            </Button>
            <Button onClick={handlePrint} variant="default" size="sm">
              Print
            </Button>
            <Button onClick={onClose} variant="outline" size="sm">
              <X className="h-4 w-4" />
            </Button>
          </div>
        </div>

        {/* Print Content */}
        <div className="flex-1 overflow-auto p-4 bg-gray-50">
          <div
            className="thermal-print-content bg-white mx-auto shadow-sm flex items-center justify-center"
            style={{
              maxWidth: '22rem',
              width: '100%',
              boxSizing: 'border-box',
              padding: '2rem 1rem',
            }}
          >
            <pre
              className="thermal-print-view font-mono text-xs leading-relaxed whitespace-pre overflow-x-hidden"
              style={{
                fontSize: '14px',
                wordBreak: 'break-word',
                margin: '0 auto',
                padding: 0,
                fontWeight: 400,
              }}
            >
              {/* Header */}
              {center('POS PERFORMANCE REPORT')}
              {'\n'}
              {center('==============================')}
              {'\n\n'}
              {/* Business Information */}
              {data.businessInfo && (
                <>
                  {center(data.businessInfo.businessTitle, 38, true)}
                  {'\n'}
                  {data.businessInfo.businessAddressInfo && (
                    <>
                      {data.businessInfo.businessAddressInfo.address1 && (
                        <>
                          {center(
                            data.businessInfo.businessAddressInfo.address1
                          )}
                          {'\n'}
                        </>
                      )}
                      {center(
                        `${data.businessInfo.businessAddressInfo.city}, ${data.businessInfo.businessAddressInfo.state} ${data.businessInfo.businessAddressInfo.zipCode}`
                      )}
                      {'\n'}
                    </>
                  )}
                  {data.businessInfo.businessAdminPhone && (
                    <>
                      {center(data.businessInfo.businessAdminPhone)}
                      {'\n'}
                    </>
                  )}
                  {'\n'}
                </>
              )}
              {/* Date Range */}
              {data.dateRange && (
                <>
                  {center(
                    `${formatDateWithoutTimezone(
                      data.dateRange.startDate
                    )} - ${formatDateWithoutTimezone(data.dateRange.endDate)}`
                  )}
                  {'\n'}
                  {data.filters?.startTime && data.filters?.endTime && (
                    <>
                      {center(
                        `${data.filters.startTime} - ${data.filters.endTime}`
                      )}
                      {'\n'}
                    </>
                  )}
                </>
              )}
              {'\n\n'}
              {line('=')}
              {'\n'}
              {center('SALES SUMMARY', 38, true)}
              {'\n'}
              {line('=')}
              {'\n\n'}
              {center(`${formatNumber(data.todaysTotalOrders || 0)} ORDERS`)}
              {'\n\n'}
              {isNewPosPerformance ? (
                <>
                  {row(
                    'Subtotal',
                    formatCurrency(data.subtotal || 0),
                    38,
                    false
                  )}
                  {'\n\n'}
                  {row('Tax', formatCurrency(data.tax || 0), 38, false)}
                  {'\n\n'}
                  {row(
                    'Adjustments & Fees',
                    formatCurrency(data.adjustmentsAndFees || 0),
                    38,
                    false
                  )}
                  {'\n\n'}
                  {data.deliveryFeeBreakdown != null && (
                    <>
                      {row(
                        'Total Delivery',
                        formatCurrency(data.totalDelivery || 0),
                        38,
                        false
                      )}
                      {'\n\n'}
                    </>
                  )}
                  {data.tipBreakdown != null && (
                    <>
                      {row(
                        'Total Tip Processed',
                        formatCurrency(data.totalTipProcessed || 0),
                        38,
                        false
                      )}
                      {'\n\n'}
                    </>
                  )}
                  {(data.totalDiscounts || 0) !== 0 && (
                    <>
                      {row(
                        'Total Discounts',
                        formatNegativeCurrency(
                          Math.abs(data.totalDiscounts || 0)
                        ),
                        38,
                        false
                      )}
                      {'\n\n'}
                    </>
                  )}
                  {data.total != null && (
                    <>
                      {'\n'}
                      {line()}
                      {'\n'}
                      {row('TOTAL', formatCurrency(data.total || 0), 38, true)}
                      {'\n\n'}
                    </>
                  )}
                  {data.refundsBreakdown != null && (
                    <>
                      {row(
                        'Total Refund',
                        formatNegativeCurrency(Math.abs(data.totalRefund || 0)),
                        38,
                        false
                      )}
                      {'\n\n'}
                    </>
                  )}
                  {(data.unpaidAmount || 0) !== 0 && (
                    <>
                      {row(
                        'Unpaid Amount',
                        formatCurrency(data.unpaidAmount || 0),
                        38,
                        false
                      )}
                      {'\n\n'}
                    </>
                  )}
                  {data.amountCollected != null && (
                    <>
                      {'\n'}
                      {line()}
                      {'\n'}
                      {row(
                        'Amount Collected',
                        formatCurrency(data.amountCollected || 0),
                        38,
                        true
                      )}
                    </>
                  )}
                  {'\n\n'}
                </>
              ) : (
                <>
                  {row(
                    'Gross Sales',
                    formatCurrency(data.subtotal || 0),
                    38,
                    false
                  )}
                  {'\n\n'}
                  {(data.totalDiscounts || 0) !== 0 && (
                    <>
                      {row(
                        'Discounts',
                        formatNegativeCurrency(Math.abs(data.totalDiscounts)),
                        38,
                        false
                      )}
                      {'\n\n'}
                    </>
                  )}
                  {data.refundsBreakdown != null && (
                    <>
                      {row(
                        'Refunds',
                        data.totalRefund
                          ? formatNegativeCurrency(Math.abs(data.totalRefund))
                          : '$0.00',
                        38,
                        false
                      )}
                      {'\n\n'}
                    </>
                  )}
                  {row(
                    'Net Sales',
                    formatCurrency(data.subtotal || 0),
                    38,
                    false
                  )}
                  {'\n\n'}
                  {data.deliveryFeeBreakdown != null && (
                    <>
                      {row(
                        'Delivery Fees',
                        formatCurrency(data.totalDeliveryFeeProcessed || 0),
                        38,
                        false
                      )}
                      {'\n\n'}
                    </>
                  )}
                  {row('Taxes', formatCurrency(data.tax || 0), 38, false)}
                  {'\n\n'}
                  {data.tipBreakdown != null && (
                    <>
                      {row(
                        'Tips',
                        formatCurrency(data.totalTipProcessed || 0),
                        38,
                        false
                      )}
                      {'\n\n'}
                    </>
                  )}
                  {data.amountCollected != null && (
                    <>
                      {line()}
                      {'\n'}
                      {row(
                        'Amount Collected',
                        formatCurrency(data.amountCollected || 0),
                        38,
                        true
                      )}
                      {'\n\n'}
                    </>
                  )}
                  {(data.unpaidAmount || 0) !== 0 && (
                    <>
                      {row(
                        'Unpaid Balance',
                        formatCurrency(data.unpaidAmount || 0),
                        38,
                        false
                      )}
                      {'\n\n'}
                    </>
                  )}
                </>
              )}
              {/* Payment Methods */}
              {isNewPosPerformance && data.paymentMethods ? (
                renderPaymentMethodsSection(
                  'PAYMENT METHODS',
                  data.paymentMethods
                )
              ) : data.moneyReport?.tenderTypes &&
                data.moneyReport.tenderTypes.length > 0 ? (
                <>
                  {line('=')}
                  {'\n'}
                  {center('PAYMENT METHODS', 38, true)}
                  {'\n'}
                  {line('=')}
                  {'\n\n'}
                  {data.moneyReport.tenderTypes.map((tender) => (
                    <>
                      {row(
                        `${tender._id.toUpperCase()}`,
                        formatCurrency(tender.totalAmount),
                        38,
                        true
                      )}
                      {'\n'}
                      {rightAlign(`${formatNumber(tender.count)} transactions`)}
                      {'\n\n'}
                    </>
                  ))}
                  {line()}
                  {'\n'}
                  {row(
                    'TOTAL',
                    formatCurrency(
                      data.moneyReport.tenderTypes.reduce(
                        (sum, tender) => sum + (tender.totalAmount || 0),
                        0
                      )
                    ),
                    38,
                    true
                  )}
                  {'\n\n'}
                </>
              ) : null}
              {isNewPosPerformance &&
                data.deliveryPaymentMethods &&
                renderPaymentMethodsSection(
                  'DELIVERY PAYMENT METHODS',
                  data.deliveryPaymentMethods
                )}

              {/* Delivery Fee Breakdown */}
              {data.deliveryFeeBreakdown && (
                <>
                  {line('=')}
                  {'\n'}
                  {center('DELIVERY FEE BREAKDOWN', 38, true)}
                  {'\n'}
                  {line('=')}
                  {'\n\n'}
                  {data.deliveryFeeBreakdown.selfDelivery !== undefined && (
                    <>
                      {row(
                        'Self Delivery',
                        formatCurrency(data.deliveryFeeBreakdown.selfDelivery),
                        38,
                        false
                      )}
                      {'\n\n'}
                    </>
                  )}
                  {data.deliveryFeeBreakdown.ordersCoDispatch !== undefined && (
                    <>
                      {row(
                        'Orders.co Dispatch',
                        formatCurrency(
                          data.deliveryFeeBreakdown.ordersCoDispatch
                        ),
                        38,
                        false
                      )}
                      {'\n\n'}
                    </>
                  )}
                  {data.deliveryFeeBreakdown.thirdParty !== undefined && (
                    <>
                      {row(
                        'Third Party',
                        formatCurrency(data.deliveryFeeBreakdown.thirdParty),
                        38,
                        false
                      )}
                      {'\n\n'}
                    </>
                  )}
                  {line()}
                  {'\n'}
                  {row(
                    'TOTAL',
                    formatCurrency(
                      (data.deliveryFeeBreakdown.selfDelivery || 0) +
                        (data.deliveryFeeBreakdown.ordersCoDispatch || 0) +
                        (data.deliveryFeeBreakdown.thirdParty || 0)
                    ),
                    38,
                    true
                  )}
                  {'\n\n'}
                </>
              )}
              {/* Tip Breakdown */}
              {isNewPosPerformance && data.tipBreakdown ? (
                <>
                  {line('=')}
                  {'\n'}
                  {center('TIP BREAKDOWN', 38, true)}
                  {'\n'}
                  {line('=')}
                  {'\n\n'}
                  {data.tipBreakdown.kitchenTip !== undefined && (
                    <>
                      {row(
                        'Kitchen',
                        formatCurrency(data.tipBreakdown.kitchenTip),
                        38,
                        true
                      )}
                      {'\n\n'}
                    </>
                  )}
                  {data.tipBreakdown.driverTip !== undefined && (
                    <>
                      {row(
                        'Driver',
                        formatCurrency(data.tipBreakdown.driverTip),
                        38,
                        true
                      )}
                      {'\n'}
                    </>
                  )}
                  {data.tipBreakdown.selfDelivery !== undefined && (
                    <>
                      {row(
                        '  Self Delivery',
                        formatCurrency(data.tipBreakdown.selfDelivery),
                        38,
                        false
                      )}
                      {'\n'}
                    </>
                  )}
                  {data.tipBreakdown.ordersCoDispatch !== undefined && (
                    <>
                      {row(
                        '  Orders.co Dispatch',
                        formatCurrency(data.tipBreakdown.ordersCoDispatch),
                        38,
                        false
                      )}
                      {'\n'}
                    </>
                  )}
                  {'\n'}
                  {line()}
                  {'\n'}
                  {row(
                    'TOTAL',
                    formatCurrency(data.totalTipProcessed || 0),
                    38,
                    true
                  )}
                  {'\n\n'}
                </>
              ) : tipSummary?.summary ? (
                <>
                  {line('=')}
                  {'\n'}
                  {center('TIP BREAKDOWN', 38, true)}
                  {'\n'}
                  {line('=')}
                  {'\n\n'}
                  {row(
                    'Kitchen',
                    formatCurrency(tipSummary.summary.totalKitchenTip),
                    38,
                    true
                  )}
                  {'\n\n'}
                  {row(
                    'Driver',
                    formatCurrency(tipSummary.summary.totalDriverTip),
                    38,
                    true
                  )}
                  {'\n'}
                  {line()}
                  {'\n'}
                  {row(
                    'TOTAL',
                    formatCurrency(tipSummary.summary.totalTip),
                    38,
                    true
                  )}
                  {'\n'}
                  {rightAlign(
                    `${formatNumber(
                      tipSummary.summary.totalOrdersWithTip || 0
                    )} orders with tips`
                  )}
                  {'\n\n'}
                </>
              ) : null}

              {/* Kitchen Tip Breakdown */}
              {data.kitchenTipBreakdown && (
                <>
                  {line('=')}
                  {'\n'}
                  {center('KITCHEN TIP BREAKDOWN', 38, true)}
                  {'\n'}
                  {line('=')}
                  {'\n\n'}
                  {center('NON-CASH', 38, true)}
                  {'\n\n'}
                  {row(
                    'Kitchen Tip Paid',
                    formatCurrency(
                      data.kitchenTipBreakdown.kitchenTipPaidNonCash || 0
                    ),
                    38,
                    false
                  )}
                  {'\n\n'}
                  {row(
                    'Kitchen Tip Refunded',
                    formatNegativeCurrency(
                      Math.abs(
                        data.kitchenTipBreakdown.kitchenTipRefundedNonCash || 0
                      )
                    ),
                    38,
                    false
                  )}
                  {'\n'}
                  {line()}
                  {'\n'}
                  {row(
                    'Kitchen Tip Collected',
                    formatCurrency(
                      data.kitchenTipBreakdown.kitchenTipCollectedNonCash || 0
                    ),
                    38,
                    true
                  )}
                  {'\n\n'}
                  {center('CASH', 38, true)}
                  {'\n\n'}
                  {row(
                    'Kitchen Tip Paid',
                    formatCurrency(
                      data.kitchenTipBreakdown.kitchenTipPaidCash || 0
                    ),
                    38,
                    false
                  )}
                  {'\n\n'}
                  {row(
                    'Kitchen Tip Refunded',
                    formatNegativeCurrency(
                      Math.abs(
                        data.kitchenTipBreakdown.kitchenTipRefundedCash || 0
                      )
                    ),
                    38,
                    false
                  )}
                  {'\n'}
                  {line()}
                  {'\n'}
                  {row(
                    'Kitchen Tip Collected',
                    formatCurrency(
                      data.kitchenTipBreakdown.kitchenTipCollectedCash || 0
                    ),
                    38,
                    true
                  )}
                  {'\n\n'}
                </>
              )}
              {/* Driver Tip Breakdown */}
              {data.driverTipBreakdown && (
                <>
                  {line('=')}
                  {'\n'}
                  {center('DRIVER TIP BREAKDOWN', 38, true)}
                  {'\n'}
                  {line('=')}
                  {'\n\n'}
                  {row(
                    'Driver Tip Paid',
                    formatCurrency(
                      (data.driverTipBreakdown.driverTipPaidNonCash || 0) +
                        (data.driverTipBreakdown.driverTipPaidCash || 0)
                    ),
                    38,
                    false
                  )}
                  {'\n\n'}
                  {row(
                    'Driver Tip Refunded',
                    formatNegativeCurrency(
                      Math.abs(
                        (data.driverTipBreakdown.driverTipRefundedNonCash ||
                          0) +
                          (data.driverTipBreakdown.driverTipRefundedCash || 0)
                      )
                    ),
                    38,
                    false
                  )}
                  {'\n'}
                  {line()}
                  {'\n'}
                  {row(
                    'Driver Tip Collected',
                    formatCurrency(
                      (data.driverTipBreakdown.driverTipCollectedNonCash || 0) +
                        (data.driverTipBreakdown.driverTipCollectedCash || 0)
                    ),
                    38,
                    true
                  )}
                  {'\n\n'}
                </>
              )}
              {/* Tax Breakdown */}
              {data.taxBreakdown && (
                <>
                  {line('=')}
                  {'\n'}
                  {center('TAX BREAKDOWN', 38, true)}
                  {'\n'}
                  {line('=')}
                  {'\n\n'}
                  {row(
                    'Tax Processed',
                    formatCurrency(data.taxBreakdown.taxProcessed || 0),
                    38,
                    false
                  )}
                  {'\n\n'}
                  {row(
                    'Prior Tax Refunded',
                    formatNegativeCurrency(
                      Math.abs(data.taxBreakdown.priorTaxRefunded || 0)
                    ),
                    38,
                    false
                  )}
                  {'\n'}
                  {line()}
                  {'\n'}
                  {row(
                    'Tax Collected',
                    formatCurrency(data.taxBreakdown.taxCollected || 0),
                    38,
                    true
                  )}
                  {'\n\n'}
                </>
              )}
              {/* Refunds Breakdown */}
              {data.refundsBreakdown && (
                <>
                  {line('=')}
                  {'\n'}
                  {center('REFUNDS BREAKDOWN', 38, true)}
                  {'\n'}
                  {line('=')}
                  {'\n\n'}
                  {row(
                    "Today's Order Refunds",
                    formatNegativeCurrency(
                      Math.abs(data.refundsBreakdown.todaysOrderRefunds || 0)
                    ),
                    38,
                    false
                  )}
                  {'\n\n'}
                  {row(
                    'Prior Order Refunds',
                    formatNegativeCurrency(
                      Math.abs(data.refundsBreakdown.priorOrderRefunds || 0)
                    ),
                    38,
                    false
                  )}
                  {'\n'}
                  {line()}
                  {'\n'}
                  {row(
                    'Total Refund Processed',
                    formatNegativeCurrency(
                      Math.abs(data.refundsBreakdown.totalRefundProcessed || 0)
                    ),
                    38,
                    true
                  )}
                  {'\n\n'}
                </>
              )}
              {/* Orders.co Dispatch Delivery Breakdown */}
              {data.ordersCoDispatchBreakdown && (
                <>
                  {line('=')}
                  {'\n'}
                  {center('ORDERS.CO DISPATCH DELIVERY', 38, true)}
                  {'\n'}
                  {line('=')}
                  {'\n\n'}
                  {row(
                    'Delivery Fee Cost',
                    formatCurrency(
                      data.ordersCoDispatchBreakdown.deliveryCost || 0
                    ),
                    38,
                    false
                  )}
                  {'\n\n'}
                  {row(
                    'Delivery Tip',
                    formatCurrency(
                      data.ordersCoDispatchBreakdown.deliveryTip || 0
                    ),
                    38,
                    false
                  )}
                  {'\n'}
                  {line()}
                  {'\n'}
                  {row(
                    'Amount Due to Orders.co',
                    formatCurrency(
                      data.ordersCoDispatchBreakdown.amountDueToOrdersCo || 0
                    ),
                    38,
                    true
                  )}
                  {'\n\n'}
                </>
              )}
              {/* Employee Sales Performance */}
              {data.employeeDetailedBreakdown?.employees &&
                data.employeeDetailedBreakdown.employees.length > 0 && (
                  <>
                    {line('=')}
                    {'\n'}
                    {center('EMPLOYEE SALES PERFORMANCE', 38, true)}
                    {'\n'}
                    {line('=')}
                    {'\n\n'}
                    {data.employeeDetailedBreakdown.employees.map(
                      (employee, idx) => (
                        <>
                          {row(
                            `${employee.employeeFirstName || ''} ${
                              employee.employeeLastName || ''
                            }`.trim() || `Employee ${idx + 1}`,
                            '',
                            38,
                            true
                          )}
                          {'\n\n'}
                          {row(
                            'Orders',
                            formatNumber(employee.totalOrders || 0),
                            38,
                            false
                          )}
                          {'\n\n'}
                          {row(
                            'Subtotal',
                            formatCurrency(employee.subtotal || 0),
                            38,
                            false
                          )}
                          {'\n\n'}
                          {row(
                            'Tax',
                            formatCurrency(employee.tax || 0),
                            38,
                            false
                          )}
                          {'\n\n'}
                          {row(
                            'Adjustments & Fees',
                            formatCurrency(employee.adjustmentsAndFees || 0),
                            38,
                            false
                          )}
                          {'\n\n'}
                          {employee.kitchenTipBreakdown && (
                            <>
                              {row(
                                'Non-cash Kitchen Tip Paid',
                                formatCurrency(
                                  employee.kitchenTipBreakdown
                                    .kitchenTipPaidNonCash || 0
                                ),
                                38,
                                false
                              )}
                              {'\n\n'}
                              {row(
                                'Non-cash Kitchen Tip Refund',
                                formatNegativeCurrency(
                                  Math.abs(
                                    employee.kitchenTipBreakdown
                                      .kitchenTipRefundedNonCash || 0
                                  )
                                ),
                                38,
                                false
                              )}
                              {'\n\n'}
                              {row(
                                'Non-cash Kitchen Tip Coll.',
                                formatCurrency(
                                  employee.kitchenTipBreakdown
                                    .kitchenTipCollectedNonCash || 0
                                ),
                                38,
                                false
                              )}
                              {'\n\n'}
                              {row(
                                'Cash Kitchen Tip Paid',
                                formatCurrency(
                                  employee.kitchenTipBreakdown
                                    .kitchenTipPaidCash || 0
                                ),
                                38,
                                false
                              )}
                              {'\n\n'}
                              {row(
                                'Cash Kitchen Tip Refunded',
                                formatNegativeCurrency(
                                  Math.abs(
                                    employee.kitchenTipBreakdown
                                      .kitchenTipRefundedCash || 0
                                  )
                                ),
                                38,
                                false
                              )}
                              {'\n\n'}
                              {row(
                                'Cash Kitchen Tip Coll.',
                                formatCurrency(
                                  employee.kitchenTipBreakdown
                                    .kitchenTipCollectedCash || 0
                                ),
                                38,
                                false
                              )}
                              {'\n\n'}
                            </>
                          )}
                          {data.amountCollected != null && (
                            <>
                              {row(
                                'Amount Collected',
                                formatCurrency(employee.amountCollected || 0),
                                38,
                                true
                              )}
                              {'\n\n'}
                            </>
                          )}
                          {row(
                            'Number of Payments',
                            formatNumber(
                              employee.detailedReport?.totalTransactions || 0
                            ),
                            38,
                            false
                          )}
                          {'\n\n'}
                          {idx <
                            data.employeeDetailedBreakdown.employees.length -
                              1 && (
                            <>
                              {line('-')}
                              {'\n\n'}
                            </>
                          )}
                        </>
                      )
                    )}
                  </>
                )}
              {/* Footer */}
              {line('=')}
              {'\n'}
              {center('END OF REPORT')}
              {'\n'}
              {center(new Date().toLocaleString())}
              {'\n'}
              {line('=')}
            </pre>
          </div>
        </div>
      </div>
    </div>
  );
}
